import React, { useEffect, useMemo, useState, useRef } from 'react';
import { getTenant } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { DownloadIcon, ChevronDownIcon } from 'lucide-react';
import { DateRangePicker } from './DateRangePicker';
import { GranularitySelector, Granularity, getAvailableGranularities, getDefaultGranularity } from './GranularitySelector';
import { PaymentMethodsFilters } from './PaymentMethodsFilters';
interface DateRange {
  start: Date;
  end: Date;
}
interface RefundsTabProps {
  dateRange: DateRange;
  granularity: Granularity;
  onDateRangeChange: (range: DateRange) => void;
  onGranularityChange: (granularity: Granularity) => void;
}
type KeyMetric = 'refund-volume' | 'refund-count' | 'refund-rate';
type PivotOption = 'Payment method' | 'Card brand' | 'Payment method category' | 'Funding source type';
// Color palette - enough for up to 7 categories
const COLOR_PALETTE = ['#4F46E5', '#6B7280', '#059669', '#D97706', '#94A3B8', '#DC2626', '#7C3AED' // violet-600
];
// Pivot breakdown data with share percentages for chart generation
const PIVOT_CONFIG: Record<PivotOption, {
  key: string;
  label: string;
  volumeShare: number;
  rateBase: number;
}[]> = {
  'Payment method': [{
    key: 'Card',
    label: 'Card',
    volumeShare: 0.725,
    rateBase: 12.5
  }, {
    key: 'ApplePay',
    label: 'Apple Pay',
    volumeShare: 0.128,
    rateBase: 14.2
  }, {
    key: 'GooglePay',
    label: 'Google Pay',
    volumeShare: 0.07,
    rateBase: 13.8
  }, {
    key: 'PromptPay',
    label: 'PromptPay',
    volumeShare: 0.044,
    rateBase: 8.5
  }, {
    key: 'Others',
    label: 'Others',
    volumeShare: 0.033,
    rateBase: 10.2
  }],
  'Card brand': [{
    key: 'Visa',
    label: 'Visa',
    volumeShare: 0.484,
    rateBase: 12.1
  }, {
    key: 'Mastercard',
    label: 'Mastercard',
    volumeShare: 0.178,
    rateBase: 12.8
  }, {
    key: 'Amex',
    label: 'American Express',
    volumeShare: 0.037,
    rateBase: 15.5
  }, {
    key: 'JCB',
    label: 'JCB',
    volumeShare: 0.02,
    rateBase: 11.2
  }, {
    key: 'UnionPay',
    label: 'UnionPay',
    volumeShare: 0.007,
    rateBase: 9.8
  }, {
    key: 'OthersNonCard',
    label: 'Others (non-card)',
    volumeShare: 0.274,
    rateBase: 13.5
  }],
  'Payment method category': [{
    key: 'Cards',
    label: 'Cards',
    volumeShare: 0.725,
    rateBase: 12.5
  }, {
    key: 'Wallets',
    label: 'Wallets',
    volumeShare: 0.198,
    rateBase: 14.0
  }, {
    key: 'BankTransfers',
    label: 'Bank transfers',
    volumeShare: 0.051,
    rateBase: 8.2
  }, {
    key: 'Others',
    label: 'Others',
    volumeShare: 0.026,
    rateBase: 10.5
  }],
  'Funding source type': [{
    key: 'Card',
    label: 'Card',
    volumeShare: 0.725,
    rateBase: 12.5
  }, {
    key: 'BankAccount',
    label: 'Bank account',
    volumeShare: 0.095,
    rateBase: 8.5
  }, {
    key: 'StoredBalance',
    label: 'Stored balance',
    volumeShare: 0.151,
    rateBase: 13.2
  }, {
    key: 'BNPLCredit',
    label: 'BNPL credit',
    volumeShare: 0.028,
    rateBase: 18.5
  }]
};
export function RefundsTab({
  dateRange,
  granularity,
  onDateRangeChange,
  onGranularityChange
}: RefundsTabProps) {
  const {
    t
  } = useTranslation('analytics');
  const [selectedMetric, setSelectedMetric] = useState<KeyMetric>('refund-volume');
  const [chartData, setChartData] = useState<any[]>([]);
  // Filter states
  const [paymentMethodCategories, setPaymentMethodCategories] = useState<string[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<string[]>([]);
  const [cardBrands, setCardBrands] = useState<string[]>([]);
  const [fundingSourceTypes, setFundingSourceTypes] = useState<string[]>([]);
  const [cardTypes, setCardTypes] = useState<string[]>([]);
  const [inputMethods, setInputMethods] = useState<string[]>([]);
  const [interactionTypes, setInteractionTypes] = useState<string[]>([]);
  const [networkTokenStatuses, setNetworkTokenStatuses] = useState<string[]>([]);
  // Pivot state
  const [pivotBy, setPivotBy] = useState<PivotOption>('Payment method');
  const [showPivotDropdown, setShowPivotDropdown] = useState(false);
  const pivotRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pivotRef.current && !pivotRef.current.contains(event.target as Node)) {
        setShowPivotDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Current pivot categories (excluding Total)
  const pivotCategories = PIVOT_CONFIG[pivotBy];
  // Color map for current pivot
  const colorMap = useMemo(() => {
    const map: Record<string, string> = {};
    pivotCategories.forEach((cat, i) => {
      map[cat.key] = COLOR_PALETTE[i % COLOR_PALETTE.length];
    });
    return map;
  }, [pivotBy]);
  // Generate mock chart data based on selected metric AND pivot
  useEffect(() => {
    const days = Math.ceil((dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24));
    const data = [];
    let baseValue = 0;
    let variance = 0;
    if (selectedMetric === 'refund-volume') {
      baseValue = 65000; // Daily refund volume
      variance = 15000;
    } else if (selectedMetric === 'refund-count') {
      baseValue = 220; // Daily refund count
      variance = 40;
    } else {
      baseValue = 12.87; // Refund rate
      variance = 2.5;
    }
    for (let i = 0; i <= days; i++) {
      const date = new Date(dateRange.start);
      date.setDate(date.getDate() + i);
      const total = Math.max(0, baseValue + (Math.random() - 0.5) * variance);
      if (selectedMetric === 'refund-rate') {
        const point: any = {
          date: date.toISOString()
        };
        pivotCategories.forEach(cat => {
          point[cat.key] = cat.rateBase + (Math.random() - 0.5) * 3;
        });
        data.push(point);
      } else {
        const point: any = {
          date: date.toISOString()
        };
        pivotCategories.forEach(cat => {
          point[cat.key] = total * cat.volumeShare * (0.95 + Math.random() * 0.1);
        });
        data.push(point);
      }
    }
    setChartData(data);
  }, [dateRange, granularity, selectedMetric, pivotBy]);
  const metrics = [{
    id: 'refund-volume',
    title: 'Refund volume',
    value: `${getTenant().currency.code} 1.82Mn`,
    change: '+12.4%',
    tooltipText: 'Total volume of refunded payments.'
  }, {
    id: 'refund-count',
    title: 'Refund count',
    value: '6,240',
    change: '+8.2%',
    tooltipText: 'Total number of refunded payments.'
  }, {
    id: 'refund-rate',
    title: 'Refund rate',
    value: '12.87%',
    change: '+1.15%',
    tooltipText: 'Percentage of successful payments that were refunded.'
  }];
  const pivotData: Record<PivotOption, any[]> = {
    'Payment method': [{
      category: 'Card',
      volume: `${getTenant().currency.code} 1.32Mn`,
      share: '72.5%',
      change: '+11.2%',
      paymentCount: '4,524',
      countShare: '72.5%',
      rate: '12.5%'
    }, {
      category: 'Apple Pay',
      volume: `${getTenant().currency.code} 233K`,
      share: '12.8%',
      change: '+18.4%',
      paymentCount: '799',
      countShare: '12.8%',
      rate: '14.2%'
    }, {
      category: 'Google Pay',
      volume: `${getTenant().currency.code} 127K`,
      share: '7.0%',
      change: '+14.1%',
      paymentCount: '437',
      countShare: '7.0%',
      rate: '13.8%'
    }, {
      category: 'PromptPay',
      volume: `${getTenant().currency.code} 80K`,
      share: '4.4%',
      change: '+5.5%',
      paymentCount: '275',
      countShare: '4.4%',
      rate: '8.5%'
    }, {
      category: 'Others',
      volume: `${getTenant().currency.code} 60K`,
      share: '3.3%',
      change: '+2.8%',
      paymentCount: '205',
      countShare: '3.3%',
      rate: '10.2%'
    }],
    'Card brand': [{
      category: 'Visa',
      volume: `${getTenant().currency.code} 881K`,
      share: '48.4%',
      change: '+10.5%',
      paymentCount: '3,020',
      countShare: '48.4%',
      rate: '12.1%'
    }, {
      category: 'Mastercard',
      volume: `${getTenant().currency.code} 324K`,
      share: '17.8%',
      change: '+12.2%',
      paymentCount: '1,111',
      countShare: '17.8%',
      rate: '12.8%'
    }, {
      category: 'American Express',
      volume: `${getTenant().currency.code} 67K`,
      share: '3.7%',
      change: '+22.4%',
      paymentCount: '231',
      countShare: '3.7%',
      rate: '15.5%'
    }, {
      category: 'JCB',
      volume: `${getTenant().currency.code} 36K`,
      share: '2.0%',
      change: '+8.1%',
      paymentCount: '125',
      countShare: '2.0%',
      rate: '11.2%'
    }, {
      category: 'UnionPay',
      volume: `${getTenant().currency.code} 13K`,
      share: '0.7%',
      change: '-2.4%',
      paymentCount: '44',
      countShare: '0.7%',
      rate: '9.8%'
    }, {
      category: 'Others (non-card)',
      volume: `${getTenant().currency.code} 499K`,
      share: '27.4%',
      change: '+15.8%',
      paymentCount: '1,709',
      countShare: '27.4%',
      rate: '13.5%'
    }],
    'Payment method category': [{
      category: 'Cards',
      volume: `${getTenant().currency.code} 1.32Mn`,
      share: '72.5%',
      change: '+11.2%',
      paymentCount: '4,524',
      countShare: '72.5%',
      rate: '12.5%'
    }, {
      category: 'Wallets',
      volume: `${getTenant().currency.code} 360K`,
      share: '19.8%',
      change: '+16.5%',
      paymentCount: '1,236',
      countShare: '19.8%',
      rate: '14.0%'
    }, {
      category: 'Bank transfers',
      volume: `${getTenant().currency.code} 93K`,
      share: '5.1%',
      change: '+4.2%',
      paymentCount: '318',
      countShare: '5.1%',
      rate: '8.2%'
    }, {
      category: 'Others',
      volume: `${getTenant().currency.code} 47K`,
      share: '2.6%',
      change: '+8.8%',
      paymentCount: '162',
      countShare: '2.6%',
      rate: '10.5%'
    }],
    'Funding source type': [{
      category: 'Card',
      volume: `${getTenant().currency.code} 1.32Mn`,
      share: '72.5%',
      change: '+11.2%',
      paymentCount: '4,524',
      countShare: '72.5%',
      rate: '12.5%'
    }, {
      category: 'Bank account',
      volume: `${getTenant().currency.code} 173K`,
      share: '9.5%',
      change: '+5.4%',
      paymentCount: '593',
      countShare: '9.5%',
      rate: '8.5%'
    }, {
      category: 'Stored balance',
      volume: `${getTenant().currency.code} 275K`,
      share: '15.1%',
      change: '+18.2%',
      paymentCount: '942',
      countShare: '15.1%',
      rate: '13.2%'
    }, {
      category: 'BNPL credit',
      volume: `${getTenant().currency.code} 51K`,
      share: '2.8%',
      change: '+28.5%',
      paymentCount: '175',
      countShare: '2.8%',
      rate: '18.5%'
    }]
  };
  const breakdownRows = [...pivotData[pivotBy], {
    category: 'Total',
    volume: `${getTenant().currency.code} 1.82Mn`,
    share: '100.00%',
    change: '+12.4%',
    paymentCount: '6,240',
    countShare: '100.00%',
    rate: '12.87%',
    isTotal: true
  }];
  const dateFormatter = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };
  const yAxisFormatter = (value: number) => {
    if (selectedMetric === 'refund-rate') {
      return `${value.toFixed(1)}%`;
    }
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value.toString();
  };
  const tooltipValueFormatter = (value: number) => {
    if (selectedMetric === 'refund-rate') {
      return `${value.toFixed(2)}%`;
    }
    if (selectedMetric === 'refund-volume') {
      return `${getTenant().currency.code} ${value.toLocaleString(undefined, {
        maximumFractionDigits: 0
      })}`;
    }
    return Math.round(value).toLocaleString();
  };
  const handleDateRangeChange = (newRange: {
    start: Date;
    end: Date;
  }) => {
    onDateRangeChange(newRange);
    const newAvailable = getAvailableGranularities(newRange);
    if (!newAvailable.includes(granularity)) {
      onGranularityChange(getDefaultGranularity(newRange));
    }
  };
  const availableGranularities = getAvailableGranularities(dateRange);
  const isGranularityHidden = availableGranularities.length <= 1;
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-white border border-gray-200 shadow-lg rounded-lg p-3 text-sm" data-id="element-2200">
          <p className="font-medium text-gray-900 mb-2" data-id="element-2201">
            {dateFormatter(label)}
          </p>
          {payload.map((entry: any, index: number) => <div key={index} className="flex items-center justify-between gap-4 mb-1" data-id="element-2202">
              <div className="flex items-center gap-2" data-id="element-2203">
                <div className="w-2 h-2 rounded-full" style={{
              backgroundColor: entry.color
            }} data-id="element-2204" />

                <span className="text-gray-600" data-id="element-2205">
                  {entry.name === 'value' ? 'Refund rate' : entry.name}
                </span>
              </div>
              <span className="font-medium text-gray-900" data-id="element-2206">
                {tooltipValueFormatter(entry.value)}
              </span>
            </div>)}
        </div>;
    }
    return null;
  };
  return <div className="space-y-6" data-id="element-2207">
      {/* Main Filters Row */}
      <div className="flex flex-wrap items-center gap-3" data-id="element-2208">
        <DateRangePicker value={dateRange} onChange={handleDateRangeChange} data-id="element-2209" />
        <GranularitySelector value={granularity} onChange={onGranularityChange} allowedGranularities={availableGranularities} hidden={isGranularityHidden} data-id="element-2210" />

        <PaymentMethodsFilters paymentMethodCategories={paymentMethodCategories} onPaymentMethodCategoriesChange={setPaymentMethodCategories} paymentMethods={paymentMethods} onPaymentMethodsChange={setPaymentMethods} cardBrands={cardBrands} onCardBrandsChange={setCardBrands} fundingSourceTypes={fundingSourceTypes} onFundingSourceTypesChange={setFundingSourceTypes} cardTypes={cardTypes} onCardTypesChange={setCardTypes} inputMethods={inputMethods} onInputMethodsChange={setInputMethods} interactionTypes={interactionTypes} onInteractionTypesChange={setInteractionTypes} networkTokenStatuses={networkTokenStatuses} onNetworkTokenStatusesChange={setNetworkTokenStatuses} data-id="element-2211" />
      </div>

      {/* Chart + Breakdown unified section */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-2212">
        <div className="p-6 pb-0" data-id="element-2213">
          <div className="flex items-center justify-between mb-4" data-id="element-2214">
            <div className="flex items-center gap-4" data-id="element-2215">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-2216">
                {t('components.RefundsTab.refunds_overview')}
              </h2>
              <div className="flex items-center gap-2" data-id="element-2217">
                <span className="text-sm text-gray-500" data-id="element-2218">
                  {t('components.CardPaymentsBreakdown.pivot_by')}
                </span>
                <div className="relative" ref={pivotRef} data-id="element-2219">
                  <button onClick={() => setShowPivotDropdown(!showPivotDropdown)} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2220">
                    <span data-id="element-2221">{pivotBy}</span>
                    <ChevronDownIcon size={14} data-id="element-2222" />
                  </button>

                  {showPivotDropdown && <div className="absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50" data-id="element-2223">
                      {(['Payment method', 'Card brand', 'Payment method category', 'Funding source type'] as PivotOption[]).map(option => <button key={option} onClick={() => {
                    setPivotBy(option);
                    setShowPivotDropdown(false);
                  }} className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${pivotBy === option ? 'text-blue-600 font-medium' : 'text-gray-700'}`} data-id="element-2224">
                          {option}
                        </button>)}
                    </div>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2" data-id="element-2225">
              <button className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-2226">
                <DownloadIcon size={14} data-id="element-2227" />
                {t('components.CardPaymentsBreakdown.download')}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-6" data-id="element-2228">
            {metrics.map(metric => <button key={metric.id} onClick={() => setSelectedMetric(metric.id as KeyMetric)} className={`text-left p-4 rounded-lg border-2 transition-all ${selectedMetric === metric.id ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2229">
                <div className="flex items-center gap-1 mb-2" data-id="element-2230">
                  <span className="text-sm text-gray-600" data-id="element-2231">{metric.title}</span>
                </div>
                <div className="text-2xl font-semibold text-gray-900 mb-1" data-id="element-2232">
                  {metric.value}
                </div>
                <div className={`text-sm ${metric.change.startsWith('-') ? 'text-red-600' : 'text-green-600'}`} data-id="element-2233">
                  {metric.change}
                </div>
              </button>)}
          </div>

          <div className="h-80 w-full mt-8" role="img" aria-label="Refunds metric chart" data-id="element-2234">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2235">
              {selectedMetric === 'refund-rate' ? <LineChart data={chartData} margin={{
              top: 10,
              right: 10,
              left: 0,
              bottom: 0
            }} data-id="element-2236">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-2237" />

                  <XAxis dataKey="date" tickFormatter={dateFormatter} axisLine={false} tickLine={false} tick={{
                fill: '#6B7280',
                fontSize: 12
              }} dy={10} data-id="element-2238" />

                  <YAxis tickFormatter={yAxisFormatter} axisLine={false} tickLine={false} tick={{
                fill: '#6B7280',
                fontSize: 12
              }} dx={-10} data-id="element-2239" />

                  <Tooltip content={<CustomTooltip data-id="element-2241" />} cursor={{
                stroke: '#E5E7EB',
                strokeWidth: 2
              }} data-id="element-2240" />

                  {pivotCategories.map(cat => <Line key={cat.key} type="monotone" dataKey={cat.key} name={cat.label} stroke={colorMap[cat.key]} strokeWidth={2} dot={false} activeDot={{
                r: 4,
                fill: colorMap[cat.key],
                stroke: '#fff',
                strokeWidth: 2
              }} data-id="element-2242" />)}
                </LineChart> : <BarChart data={chartData} margin={{
              top: 10,
              right: 10,
              left: 0,
              bottom: 0
            }} data-id="element-2243">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-2244" />

                  <XAxis dataKey="date" tickFormatter={dateFormatter} axisLine={false} tickLine={false} tick={{
                fill: '#6B7280',
                fontSize: 12
              }} dy={10} data-id="element-2245" />

                  <YAxis tickFormatter={yAxisFormatter} axisLine={false} tickLine={false} tick={{
                fill: '#6B7280',
                fontSize: 12
              }} dx={-10} data-id="element-2246" />

                  <Tooltip content={<CustomTooltip data-id="element-2248" />} cursor={{
                fill: '#F3F4F6'
              }} data-id="element-2247" />

                  {pivotCategories.map((cat, i) => <Bar key={cat.key} dataKey={cat.key} name={cat.label} stackId="a" fill={colorMap[cat.key]} radius={i === pivotCategories.length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0]} data-id="element-2249" />)}
                </BarChart>}
            </ResponsiveContainer>
          </div>

          <div className="flex items-center justify-center gap-6 mt-6" data-id="element-2250">
            {pivotCategories.map(cat => <div key={cat.key} className="flex items-center gap-2" data-id="element-2251">
                <div className="w-3 h-3 rounded-full" style={{
              backgroundColor: colorMap[cat.key]
            }} data-id="element-2252" />

                <span className="text-xs text-gray-600" data-id="element-2253">{cat.label}</span>
              </div>)}
          </div>
        </div>

        {/* Breakdown Table - inside same card */}
        <div className="border-t border-gray-200 mt-6" data-id="element-2254">
          <div className="overflow-x-auto" data-id="element-2255">
            <table className="w-full text-sm text-left" data-id="element-2256">
              <thead className="text-xs text-gray-500 bg-gray-50 border-y border-gray-200" data-id="element-2257">
                <tr data-id="element-2258">
                  <th className="px-4 py-3 font-medium" data-id="element-2259">
                    {pivotBy === 'Payment method category' ? 'Category' : pivotBy === 'Funding source type' ? 'Funding source' : pivotBy}
                  </th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-2260">
                    {t('components.PaymentVolumeWidget.volume')}
                  </th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-2261">
                    {t('components.BreakdownTable.share_of_volume')}
                  </th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-2262">
                    {t('components.RefundsTab.refund_count')}
                  </th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-2263">
                    {t('components.PaymentMethodsTab.share_of_count')}
                  </th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-2264">
                    {t('components.RefundsTab.refund_rate')}
                  </th>
                  <th className="px-4 py-3 font-medium text-right" data-id="element-2265">
                    {t('components.BreakdownTable.change')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200" data-id="element-2266">
                {breakdownRows.map((row, index) => <tr key={index} className={row.isTotal ? 'bg-gray-50 font-medium' : 'hover:bg-gray-50'} data-id="element-2267">
                    <td className="px-4 py-3 text-gray-900" data-id="element-2268">
                      <div className="flex items-center gap-2" data-id="element-2269">
                        {!row.isTotal && pivotCategories[index] && <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{
                      backgroundColor: colorMap[pivotCategories[index]?.key]
                    }} data-id="element-2270" />}
                        {row.category}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900" data-id="element-2271">
                      {row.volume}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500" data-id="element-2272">
                      {row.share}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900" data-id="element-2273">
                      {row.paymentCount}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-500" data-id="element-2274">
                      {row.countShare}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900" data-id="element-2275">
                      {row.rate}
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-2276">
                      {row.change && <span className={row.change.startsWith('+') ? 'text-green-600' : 'text-red-600'} data-id="element-2277">
                          {row.change}
                        </span>}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>;
}